import { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useCartContext } from '../provider/cart/CartContext';
import { isFloat } from '../utils/assertions';
import { getCurrentLocale } from '../utils/getCurrentLocale';

const maxAboveValue = 110000;

export const useShippingCost = () => {
  const { cartData } = useCartContext();
  const { currency } = useSelector((state) => state.app.context);

  const [price, setPrice] = useState(null);
  const [freeAbove, setFreeAbove] = useState(null);
  const [deficiencyToFreeShipping, setDeficiencyToFreeShipping] =
    useState(null);

  const shippingInfo = cartData?.cart?.shippingInfo;
  const itemsTotal =
    cartData?.cart?.projectSpecificData?.original_total -
    (shippingInfo?.price ?? 0);
  const { availableShippingMethods } = cartData;

  const defaultShippingMethod =
    availableShippingMethods && availableShippingMethods[0];

  const currentLocale = getCurrentLocale();
  const formatLocale = `en-${currentLocale}`;

  const formatPrice = (price) => {
    return isFloat(price)
      ? price.toLocaleString(formatLocale, { style: 'currency', currency })
      : (price / 100).toLocaleString(formatLocale, {
          style: 'currency',
          currency,
        });
  };

  const getFreeAbove = () => {
    if (freeAbove === null  || freeAbove === undefined) {
      return null;
    }

    if (freeAbove === 0) {
      return `$0`;
    }

    if (freeAbove > maxAboveValue) {
      return ' ';
    }

    return formatPrice(freeAbove);
  }

  // This is running on each update in the cart
  useEffect(() => {
    if (availableShippingMethods && availableShippingMethods.length > 0) {
      getShippingInfo();
    }
  }, [itemsTotal, availableShippingMethods]);

  // Update the price and freeAbove values
  const updateState = (method) => {
    const standardRate = method?.rates?.[0];
    const {
      price = null,
      projectSpecificData: { freeAbove: freeAboveData },
    } = standardRate;
    const priceValue = cartData?.cart?.shippingMethod ? shippingInfo.price : price;
    setDeficiencyToFreeShipping((freeAboveData - itemsTotal) / 100);
    setFreeAbove(freeAboveData);
    if (itemsTotal >= freeAboveData) {
      setPrice(0);
    } else {
      setPrice(priceValue);
    }
  };

  // We need to turn this into callback to always have update
  // dependent data
  const getShippingInfo = useCallback(() => {
    if (shippingInfo) {
      const selectedShippingMethod =
        (availableShippingMethods &&
          availableShippingMethods.find(
            (m) => m?.shippingMethodId === shippingInfo?.shippingMethodId,
          )) ||
        availableShippingMethods[0];
      return updateState(selectedShippingMethod);
    }
    if (defaultShippingMethod) {
      return updateState(defaultShippingMethod);
    }
  }, [availableShippingMethods, shippingInfo, defaultShippingMethod]);

  const currentMethodTitle = useMemo(() => {
    const currentMethod = availableShippingMethods?.find((x) => x.shippingMethodId === shippingInfo?.shippingMethodId);
    return currentMethod?.projectSpecificData?.title;
  }, [availableShippingMethods, shippingInfo]);

  return {
    price,
    priceFormatted: price > 0 ? formatPrice(price)?.replace(/\..*/, '') : 'Free',
    freeAbove,
    freeAboveFormatted: getFreeAbove(),
    deficiencyToFreeShipping,
    shippingInfo,
    currentMethodTitle,
  };
};
